import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { DateTime } from 'luxon';
import { IEmployeeSCDItem } from '../interfaces/IEmployeeSCDItem';
import { IOrgUnit } from '../interfaces/orgunit/IOrgUnit';
import { IUser } from '../interfaces/IUser';
import { INewsCreateUpdateItem } from '../interfaces/news/INews';
import { NotificationService } from '../services/notification.service';
import { INotification } from '../interfaces/INotification';
import { NEWS_TYPES } from '../enums/NewsTypes.enum';
import { USER_ROLES } from '../enums/UserRoles.enum';
import { UserOperations } from './user.operations';

export class NewsOperations {
  public static buildCreateNewsForm(currentUser: IUser): FormGroup {
    const isUserTechnicalController = UserOperations.isUserTechnicalController(currentUser);
    const isUserGroupLeader = UserOperations.isUserGroupLead(currentUser);
    return new FormGroup({
      createdBy: new FormControl<IEmployeeSCDItem>(
        {
          value: this.getCurrentEmployeeSCDItem(currentUser),
          disabled: true,
        },
        Validators.required,
      ),
      title: new FormControl<string>(
        {
          value: null,
          disabled: false,
        },
        Validators.required,
      ),
      description: new FormControl<string>(
        {
          value: null,
          disabled: false,
        },
        Validators.required,
      ),
      startDate: new FormControl<Date | string>(
        {
          value: this.getCurrentTimeWithHoursAndMinutes(new Date()),
          disabled: false,
        },
        [Validators.required],
      ),
      endDate: new FormControl<Date | string>(
        {
          value: this.getEndOfDayInUTC(new Date()),
          disabled: false,
        },
        [Validators.required],
      ),
      isForAll: new FormControl<boolean>({
        value: false,
        disabled: !isUserTechnicalController,
      }),
      group: new FormControl<string>({
        value: isUserTechnicalController ? null : currentUser.team.groupName,
        disabled: !isUserTechnicalController && !isUserGroupLeader,
      }),
      team: new FormControl<string>({
        value: isUserTechnicalController ? null : currentUser.team.teamName,
        disabled: !isUserTechnicalController && !isUserGroupLeader,
      }),
      userRoles: new FormControl<USER_ROLES[]>({
        value: [],
        disabled: !isUserTechnicalController,
      }),
      newsType: new FormControl<NEWS_TYPES>({
        value: !isUserTechnicalController ? NEWS_TYPES.GROUP_AND_TEAM : null,
        disabled: !isUserTechnicalController,
      }),
    });
  }

  public static getSelectableGroups(activeOrgUnits: IOrgUnit[], currentUser: IUser): string[] {
    const activeGroups = Array.from(new Set(activeOrgUnits.map(orgUnit => orgUnit.groupName)));
    if (!UserOperations.isUserGroupLead(currentUser)) {
      return activeGroups;
    }

    const ledGroups = [currentUser.team?.groupName, ...(currentUser.additionalLedGroups ?? [])];
    return activeGroups.filter(group => ledGroups.includes(group));
  }

  public static getNewsCreateItemFromForm(form: FormGroup, orgUnits: IOrgUnit[]): INewsCreateUpdateItem {
    const newsCreateItem: INewsCreateUpdateItem = {
      title: form.get('title').value,
      description: form.get('description').value,
      startDate: this.getCurrentTimeWithHoursAndMinutes(new Date(form.get('startDate').value)),
      endDate: this.getEndOfDayInUTC(new Date(form.get('endDate').value)),
      isForAll: form.get('isForAll').getRawValue(),
      orgUnit:
        form.get('group').getRawValue() && form.get('team').getRawValue()
          ? orgUnits.find(ou => ou.groupName === form.get('group').getRawValue() && ou.team === form.get('team').getRawValue())
          : null,
      userRoleNames: form.get('userRoles').getRawValue(),
      createdBy: form.get('createdBy').getRawValue(),
      newsType: form.get('newsType').getRawValue(),
    };
    return newsCreateItem;
  }

  public static showCreateNewsSuccessNotification(notificationService: NotificationService): void {
    const notification: INotification = {
      type: 'Success',
      title: 'navigation.news',
      desc: 'news.notifications.createNewsSuccess',
    };
    notificationService.showMessage(notification);
  }

  public static showCreateNewsErrorNotification(notificationService: NotificationService): Observable<null> {
    const notification: INotification = {
      type: 'Error',
      title: 'navigation.news',
      desc: 'news.notifications.createNewsError',
    };
    notificationService.showMessage(notification);
    return of(null);
  }

  private static getCurrentEmployeeSCDItem(user: IUser): IEmployeeSCDItem {
    return {
      scdName: user.scdName,
      scdEMail: user.scdEMail,
      scdGid: user.gid,
    };
  }

  private static getCurrentTimeWithHoursAndMinutes(date: Date): string {
    const startDateValue = date;
    const now = DateTime.now();
    const today = DateTime.now().startOf('day');
    const inputDate = DateTime.fromJSDate(startDateValue).startOf('day');

    // If the date is today, use current time; otherwise use the ISO String from the beginning of the day in current timezone method
    if (inputDate.equals(today)) {
      const startDateWithCurrentTime = DateTime.fromJSDate(startDateValue)
        .set({
          hour: now.hour,
          minute: now.minute,
          second: now.second,
          millisecond: now.millisecond,
        })
        .toUTC()
        .toISO();
      return startDateWithCurrentTime;
    } else {
      return startDateValue.toISOString();
    }
  }

  private static getEndOfDayInUTC(date: Date): string {
    const endDateWithEndOfDay = DateTime.fromJSDate(date)
      .set({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999,
      })
      .toUTC()
      .toISO();
    return endDateWithEndOfDay;
  }
}
